import type { FinalResume } from '../types/resume';

/**
 * Export resume as plain text
 */
export function exportAsText(resume: FinalResume): string {
  const lines: string[] = [];

  // Summary
  if (resume.summary) {
    lines.push('PROFESSIONAL SUMMARY');
    lines.push(resume.summary);
    lines.push('');
  }

  // Experience
  if (resume.experience.length > 0) {
    lines.push('EXPERIENCE');
    for (const exp of resume.experience) {
      lines.push(`${exp.title} | ${exp.company}`);
      lines.push(`${exp.start_date} - ${exp.end_date || 'Present'}${exp.location ? ` | ${exp.location}` : ''}`);
      for (const bullet of exp.bullets) {
        lines.push(`  - ${bullet.text}`);
      }
      lines.push('');
    }
  }

  // Skills
  const skillEntries = Object.entries(resume.skills);
  if (skillEntries.length > 0) {
    lines.push('SKILLS');
    for (const [category, list] of skillEntries) {
      lines.push(`${category}: ${list.join(', ')}`);
    }
    lines.push('');
  }

  // Education
  if (resume.education.length > 0) {
    lines.push('EDUCATION');
    for (const edu of resume.education) {
      lines.push(`${edu.degree}${edu.field ? `, ${edu.field}` : ''} — ${edu.institution}${edu.year ? ` (${edu.year})` : ''}`);
    }
    lines.push('');
  }

  // Certifications
  if (resume.certifications.length > 0) {
    lines.push('CERTIFICATIONS');
    for (const cert of resume.certifications) {
      lines.push(`${cert.name}${cert.issuer ? ` — ${cert.issuer}` : ''}${cert.year ? ` (${cert.year})` : ''}`);
    }
  }

  return lines.join('\n');
}

/**
 * Download text content as a file
 */
export function downloadFile(content: string, filename: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Export as PDF via browser print
 */
export function exportAsPDF() {
  window.print();
}

/**
 * Export as plain text file
 */
export function exportAsTextFile(resume: FinalResume, filename: string) {
  const text = exportAsText(resume);
  downloadFile(text, filename, 'text/plain');
}

/**
 * Export as Word-compatible HTML file
 * Uses HTML format that Word can open, avoiding the docx dependency
 */
export function exportAsWord(resume: FinalResume, filename: string) {
  const html = `
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
  body { font-family: Calibri, sans-serif; font-size: 11pt; line-height: 1.4; margin: 1in; color: #333; }
  h1 { font-size: 16pt; color: #1a1a1a; margin-bottom: 4pt; }
  h2 { font-size: 13pt; color: #2563eb; border-bottom: 1pt solid #2563eb; padding-bottom: 2pt; margin-top: 14pt; margin-bottom: 6pt; }
  .job-header { font-weight: bold; margin-top: 8pt; }
  .job-dates { color: #666; font-size: 10pt; }
  ul { margin: 4pt 0; padding-left: 18pt; }
  li { margin-bottom: 3pt; }
  .skills-category { font-weight: bold; }
</style>
</head>
<body>
${resume.summary ? `<h2>Professional Summary</h2><p>${escapeHtml(resume.summary)}</p>` : ''}

${resume.experience.length > 0 ? `
<h2>Experience</h2>
${resume.experience.map(exp => `
<p class="job-header">${escapeHtml(exp.title)} | ${escapeHtml(exp.company)}</p>
<p class="job-dates">${escapeHtml(exp.start_date)} - ${escapeHtml(exp.end_date || 'Present')}${exp.location ? ` | ${escapeHtml(exp.location)}` : ''}</p>
<ul>
${exp.bullets.map(b => `<li>${escapeHtml(b.text)}</li>`).join('\n')}
</ul>
`).join('')}` : ''}

${Object.keys(resume.skills).length > 0 ? `
<h2>Skills</h2>
${Object.entries(resume.skills).map(([cat, list]) => `
<p><span class="skills-category">${escapeHtml(cat)}:</span> ${list.map(escapeHtml).join(', ')}</p>
`).join('')}` : ''}

${resume.education.length > 0 ? `
<h2>Education</h2>
${resume.education.map(edu => `
<p>${escapeHtml(edu.degree)}${edu.field ? `, ${escapeHtml(edu.field)}` : ''} — ${escapeHtml(edu.institution)}${edu.year ? ` (${escapeHtml(edu.year)})` : ''}</p>
`).join('')}` : ''}

${resume.certifications.length > 0 ? `
<h2>Certifications</h2>
${resume.certifications.map(cert => `
<p>${escapeHtml(cert.name)}${cert.issuer ? ` — ${escapeHtml(cert.issuer)}` : ''}${cert.year ? ` (${escapeHtml(cert.year)})` : ''}</p>
`).join('')}` : ''}
</body>
</html>`;

  downloadFile(html, filename.replace(/\.docx$/, '.doc'), 'application/msword');
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
